import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CardStyleInterpolators,
  createStackNavigator,
  StackNavigationOptions,
} from '@react-navigation/stack'
import RNBootSplash from 'react-native-bootsplash'
import {
  DefaultTheme,
  NavigationContainer,
  NavigationState,
  PartialState,
} from '@react-navigation/native'
import { useTheme } from '@ui-kitten/components'
import { IConfig } from 'unleash-proxy-client'
import { useFlagsStatus, useUnleashClient } from '@unleash/proxy-client-react'
import { AppState } from 'react-native'
import Heap from '@src/vendor/react-native-heap'
import { useIsDarkMode } from '@config/theme'
import { createNavigator } from '@src/navigation/containers/Navigator'
import { AuthWithThirdPartyNavigator, getAppNavigator } from '@src/navigation'
import { Analytics, App, Bugsnag, CustomEventTypes, Device } from '@config'
import { isMfaRequiredSelector, lastRequestStatusSelector, userSelector } from '@src/selectors/app'
import { useImpersonationContext } from '@src/config/impersonation'
import { Storage } from '@src/utils'
import {
  AddressModal,
  CreatePaymentMethodScreen,
  DiscountCodeModal,
  PermissionsCarousel,
  WalkthroughCarouselScreen,
} from '@src/screens'
import { Login } from '@src/models/app.types'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { isIntercomIneligibleRouteName, useSetIntercomUser } from '@src/hooks/useSetIntercomUser'
import { NavigationRouteConfig } from '@src/utils/analytics'
import { navigationRef } from '@src/config/navigation'
import { useHasLargeScreen } from '@src/config/device'
import { useReducedMotion } from '@src/config/accessibility'
import { Scene, SceneContext } from '@src/config/scene'
import { ActionBottomSheetProvider } from '@components'
import { useScreenCaptureTracking } from '@src/hooks/useScreenCaptureTracking'
import { useStorageValue } from '@src/utils/storage'
import { useIframeImpersonation } from '@src/hooks/useIframeImpersonation'
import { ActionBottomSheet } from '@src/components/ActionBottomSheet/ActionBottomSheet'
import { useShouldShowByosOnboarding } from '@src/screens/OwnSensorOnboarding/hooks'
import { NoraStreamChatProvider } from '@src/screens/Nora/context/NoraStreamChatContext'
import { CancelModal } from '@src/components/CancelModal'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { MfaChallengeScreen } from '@screens/MfaChallenge/components/MfaChallengeScreen.tsx'
import { UhcMemberAppAccessBlockedScreen } from '@screens/Uhc/components/UhcMemberAppAccessBlockedScreen.tsx'
import { DietitianAddonOrderConfirmedScreen } from '@src/screens/Marketplace'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { upsellOffersSelector } from '@src/selectors/unifiedOnboarding'
import { SensorSelectionScreen } from '@src/screens/OwnSensorOnboarding/SensorSelectionScreen'
import { FreeVideoCallScreen } from '@src/screens/NutritionistHub'
import { AppStackParamList } from '../types'
import { fadeFromBottomModalOptions, LAZY_SCREEN_PATHS } from '../utils'
import { DrawerRoute, useDrawerRoutes } from '../components/drawer/hooks'
import { linking } from '../linking'

const AppExplorationCarousel = lazy(LAZY_SCREEN_PATHS.AppExplorationCarousel)
const AppExplorationPrompt = lazy(LAZY_SCREEN_PATHS.AppExplorationPrompt)
const OutdatedScreen = lazy(LAZY_SCREEN_PATHS.OutdatedScreen)
const QuestionnaireNavigator = lazy(LAZY_SCREEN_PATHS.Questionnaire)
const InsuranceQuestionnaireNavigator = lazy(LAZY_SCREEN_PATHS.InsuranceQuestionnaire)
const CameraScreen = lazy(LAZY_SCREEN_PATHS.Camera)
const CoachInfoModal = lazy(LAZY_SCREEN_PATHS.CoachInfoModal)
const OnboardingNavigator = lazy(LAZY_SCREEN_PATHS.OnboardingNavigator)
const LegalScreen = lazy(LAZY_SCREEN_PATHS.Legal)
const SupportAnswer = lazy(LAZY_SCREEN_PATHS.SupportAnswer)
const SupportArchive = lazy(LAZY_SCREEN_PATHS.SupportArchive)
const SupportHub = lazy(LAZY_SCREEN_PATHS.SupportHub)
const VideoCallConfirmed = lazy(LAZY_SCREEN_PATHS.VideoCallConfirmed)
const UnifiedOnboardingAppointmentOfferConfirmation = lazy(
  LAZY_SCREEN_PATHS.UnifiedOnboardingAppointmentOfferConfirmation,
)
const UnifiedOnboardingConfirmation = lazy(LAZY_SCREEN_PATHS.UnifiedOnboardingConfirmation)
const UpsellOfferScreen = lazy(LAZY_SCREEN_PATHS.UpsellOffer)
const UpsellOfferConfirmationScreen = lazy(LAZY_SCREEN_PATHS.UpsellOfferConfirmation)
const SteloPaymentSuccessfulScreen = lazy(LAZY_SCREEN_PATHS.SteloPaymentSuccessful)

const AppContainer = Heap.withReactNavigationAutotrack(NavigationContainer)
const BugsnagNavigationContainer = Bugsnag.createNavigationContainer(AppContainer)
const Stack = createStackNavigator()

const getCurrentSceneName = ({
  isAuth,
  isOutdated,
  isMfaRequired,
  shouldShowPurchaseFlow,
  shouldShowUnifiedOnboarding,
  uhcMemberAppAccessBlocked,
}: {
  isAuth: boolean
  isOutdated: boolean
  isMfaRequired: boolean
  shouldShowPurchaseFlow: boolean
  shouldShowUnifiedOnboarding: boolean
  uhcMemberAppAccessBlocked: boolean
}) => {
  if (isOutdated) {
    return Scene.Outdated
  }

  if (!isAuth) {
    return Scene.Auth
  }

  if (isMfaRequired) {
    return Scene.MfaChallenge
  }

  if (shouldShowPurchaseFlow || shouldShowUnifiedOnboarding) {
    return Scene.Onboarding
  }

  if (uhcMemberAppAccessBlocked) {
    return Scene.UhcMemberAppAccessBlocked
  }

  return Scene.AppAccess
}

const getCurrentScene = ({
  sceneName,
  screenOptions,
  hasLargeScreen,
  drawerRoutes,
}: {
  sceneName: Scene
  screenOptions: StackNavigationOptions
  hasLargeScreen: boolean
  drawerRoutes: DrawerRoute[]
}) => {
  switch (sceneName) {
    case Scene.Outdated:
      return <Stack.Screen name="OutdatedScreen" component={OutdatedScreen} />
    case Scene.Auth:
      return (
        <>
          <Stack.Screen
            name="Auth"
            options={screenOptions}
            component={AuthWithThirdPartyNavigator}
          />
          <Stack.Screen name="Walkthrough" component={WalkthroughCarouselScreen} />
        </>
      )
    case Scene.Onboarding:
      return (
        <>
          <Stack.Screen name="Onboarding" options={screenOptions} component={OnboardingNavigator} />
          <Stack.Screen name="CancelModal" component={CancelModal} />
        </>
      )
    case Scene.MfaChallenge:
      return <Stack.Screen name="MfaChallenge" component={MfaChallengeScreen} />
    case Scene.UhcMemberAppAccessBlocked:
      return (
        <Stack.Screen
          name="UhcMemberAppAccessBlocked"
          component={UhcMemberAppAccessBlockedScreen}
        />
      )
    default:
      return getAppNavigator(Stack, screenOptions, hasLargeScreen, drawerRoutes)
  }
}

const getInitialRouteName = ({
  isAuth,
  isMfaRequired,
  isOutdated,
  shouldShowPurchaseFlow,
  shouldShowByosOnboarding,
  shouldShowUnifiedOnboarding,
  shouldShowWalkthrough,
  shouldShowUpsellOffers,
}: {
  isAuth: boolean
  isMfaRequired: boolean
  isOutdated: boolean
  shouldShowPurchaseFlow: boolean
  shouldShowByosOnboarding: boolean
  shouldShowUnifiedOnboarding: boolean
  shouldShowWalkthrough: boolean
  shouldShowUpsellOffers: boolean
}) => {
  if (isOutdated) {
    return 'OutdatedScreen'
  }

  if (!isAuth) {
    if (shouldShowWalkthrough) {
      return 'Walkthrough'
    }

    return 'Auth'
  }

  if (isMfaRequired) {
    return 'MfaChallenge'
  }

  if (shouldShowPurchaseFlow || shouldShowUnifiedOnboarding) {
    return 'Onboarding'
  }

  if (shouldShowUpsellOffers) {
    return 'UpsellOffer'
  }

  if (shouldShowByosOnboarding) {
    return 'ConfirmSensorSelection'
  }

  return 'Drawer'
}

const MainApp = () => {
  const dispatch = useDispatch()
  const isMfaRequired = !!useSelector(isMfaRequiredSelector)
  const lastRequestStatus = useSelector(lastRequestStatusSelector)
  const user = useSelector(userSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const isAuth = !!user
  const uhcMemberAppAccessBlocked = user?.uhcMember?.appAccessBlocked || false

  const userId = user?.id
  const userEmail = user?.email

  const unleashClient = useUnleashClient()

  const isImpersonating = useImpersonationContext()

  const [adminLogin] = useStorageValue<Login>('admin_login')
  const adminId = adminLogin?.user?.id

  const [walkthroughShown] = useStorageValue<boolean>(Storage.WALKTHROUGH_SHOWN_KEY)

  useIframeImpersonation(adminLogin)

  useEffect(() => {
    const context: IConfig['context'] = {
      userId,
      properties: {
        appVersion: App.version,
        appBuildEnv: App.build.env,
        isImpersonating: isImpersonating.toString(),
        isAdmin: (!!adminId).toString(),
        adminId: adminId || '',
        userEmail: userEmail || '',
        userEmailDomain: userEmail ? userEmail.split('@')[1] : '',
      },
    }
    unleashClient.updateContext(context)
  }, [userId, isImpersonating, adminId, userEmail, unleashClient])

  useEffect(() => {
    // start unleash client
    unleashClient.start()

    unleashClient.on('impression', (event: any) => {
      const { eventType, enabled, featureName: featureFlag } = event

      if (eventType === 'isEnabled') {
        Analytics.track(
          CustomEventTypes.FeatureFlagFetched,
          { enabled, featureFlag },
          `${CustomEventTypes.FeatureFlagFetched}/${featureFlag}`,
        )
      }
    })

    // do not ping unleash in background
    const changeSubscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active') {
        unleashClient.start()
      } else if (nextAppState.match(/inactive|background/)) {
        unleashClient.stop()
      }
    })

    return () => {
      changeSubscription.remove()
      unleashClient.stop()
    }
  }, [unleashClient])

  const upsellOffers = useSelector(upsellOffersSelector)

  const shouldShowUpsellOffers = upsellOffers.some(
    (upsellOffer) => !upsellOffer.skipped && !upsellOffer.completed,
  )

  const shouldShowPurchaseFlow = useShouldShowPurchaseFlow()
  const shouldShowByosOnboarding = useShouldShowByosOnboarding() && !shouldShowUpsellOffers

  const shouldShowUnifiedOnboarding = uiFlags.showUnifiedOnboarding || false

  const isOnboardingInProgress =
    shouldShowPurchaseFlow || shouldShowByosOnboarding || shouldShowUnifiedOnboarding

  useEffect(() => {
    dispatch({
      type: 'app/updateOnboardingState',
      payload: { isInProgress: isOnboardingInProgress },
    })
  }, [dispatch, isOnboardingInProgress])

  const hasLargeScreen = useHasLargeScreen()
  const drawerRoutes = useDrawerRoutes()

  const isOutdated = lastRequestStatus === 426

  const reduceMotionEnabled = useReducedMotion()

  const screenOptions: StackNavigationOptions = {
    headerShown: false,
    cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
  }

  if (reduceMotionEnabled) {
    screenOptions.animation = 'none'
  }

  const currentSceneName = getCurrentSceneName({
    isAuth,
    isOutdated,
    isMfaRequired,
    shouldShowPurchaseFlow,
    shouldShowUnifiedOnboarding,
    uhcMemberAppAccessBlocked,
  })

  const currentScene = getCurrentScene({
    sceneName: currentSceneName,
    screenOptions,
    hasLargeScreen,
    drawerRoutes,
  })

  const initialRouteName = getInitialRouteName({
    isAuth,
    isMfaRequired,
    isOutdated,
    shouldShowPurchaseFlow,
    shouldShowByosOnboarding,
    shouldShowUnifiedOnboarding,
    shouldShowWalkthrough: !walkthroughShown && !Device.web,
    shouldShowUpsellOffers,
  })

  const hasAppAccess = isAuth && !shouldShowPurchaseFlow && !isMfaRequired

  useEffect(() => {
    if (!hasAppAccess) {
      return
    }

    if (!userId) {
      return
    }

    const appOpenCountKey = `${Storage.APP_OPEN_COUNT_KEY}_${userId}`

    const previousAppOpeningsCount = Storage.get(appOpenCountKey, 0)
    Storage.set(appOpenCountKey, previousAppOpeningsCount + 1)
  }, [hasAppAccess, userId])

  const routeNameRef = useRef<NavigationRouteConfig['name'] | null>(null)
  const [hasIneligibleIntercomRoute, setHasIneligibleIntercomRoute] = useState(
    isIntercomIneligibleRouteName(initialRouteName),
  )

  const setRouteName = useCallback((name: NavigationRouteConfig['name']) => {
    routeNameRef.current = name
    setHasIneligibleIntercomRoute(isIntercomIneligibleRouteName(name))
  }, [])

  useScreenCaptureTracking(routeNameRef)

  useSetIntercomUser({ hasAppAccess, hasIneligibleIntercomRoute })

  const theme = useTheme()
  const isDarkMode = useIsDarkMode()

  const linkingOptions = useMemo(() => linking({ initialRouteName, hasLargeScreen, isAuth }), [
    initialRouteName,
    hasLargeScreen,
    isAuth,
  ])

  const onReady = useCallback(() => {
    Analytics.screenView(initialRouteName)
    setRouteName(initialRouteName)

    RNBootSplash.hide({ fade: true })
  }, [initialRouteName, setRouteName])

  const onStateChange = useCallback(
    (state: Readonly<NavigationState> | undefined) => {
      const previousRouteName = routeNameRef.current

      const getActiveRoute = (
        state?: NavigationState | PartialState<NavigationState>,
      ): NavigationRouteConfig | null => {
        if (!state || typeof state.index !== 'number') {
          return null
        }

        const route = state.routes[state.index]

        if (route.state) {
          return getActiveRoute(route.state)
        }

        return route as NavigationRouteConfig
      }

      const currentRoute = getActiveRoute(state)

      if (currentRoute && currentRoute.name !== previousRouteName) {
        Analytics.screenView(currentRoute.name, { ...currentRoute.params })
        setRouteName(currentRoute.name)
      }
    },
    [setRouteName],
  )

  const navigationTheme: ReactNavigation.Theme = useMemo(
    () => ({
      dark: isDarkMode,
      colors: {
        primary: theme['theme.primary.base'],
        background: theme['theme.background'],
        card: theme['theme.surface.base'],
        text: theme['theme.text.primary'],
        border: theme['theme.surface.base'],
        notification: theme['theme.error.base'],
      },
      fonts: DefaultTheme.fonts,
    }),
    [isDarkMode, theme],
  )

  const { flagsReady, flagsError } = useFlagsStatus()
  const flagsLoaded = flagsReady || flagsError

  if (!flagsLoaded) {
    return <LoadingContainer />
  }

  return (
    <BugsnagNavigationContainer
      ref={navigationRef}
      linking={linkingOptions}
      navigationInChildEnabled
      onReady={onReady}
      onStateChange={onStateChange}
      theme={navigationTheme}
    >
      <SceneContext.Provider value={{ scene: currentSceneName }}>
        <ActionBottomSheetProvider>
          <NoraStreamChatProvider>
            <Stack.Navigator
              initialRouteName={initialRouteName}
              screenOptions={screenOptions}
              screenLayout={({ children }) => (
                <Suspense fallback={<LoadingContainer />}>{children}</Suspense>
              )}
            >
              {currentScene}

              <Stack.Screen
                options={{
                  cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
                  presentation: 'transparentModal',
                }}
                name="Camera"
                component={CameraScreen}
              />
              <Stack.Screen
                name="CoachInfoModal"
                component={CoachInfoModal}
                options={fadeFromBottomModalOptions}
              />
              <Stack.Screen
                name="ActionBottomSheet"
                component={ActionBottomSheet}
                options={fadeFromBottomModalOptions}
              />
              <Stack.Screen
                name="Legal"
                component={LegalScreen}
                options={fadeFromBottomModalOptions}
              />
              <Stack.Screen name="OwnSensorSelection" component={SensorSelectionScreen} />

              {isAuth && (
                <>
                  <Stack.Screen name="ChangeAddress" component={AddressModal} />
                  <Stack.Screen name="CreatePaymentMethod" component={CreatePaymentMethodScreen} />
                  <Stack.Screen
                    name="SteloPaymentSuccessful"
                    component={SteloPaymentSuccessfulScreen}
                  />
                  <Stack.Screen
                    name="UnifiedOnboardingConfirmation"
                    component={UnifiedOnboardingConfirmation}
                  />
                  <Stack.Screen name="VideoCallConfirmed" component={VideoCallConfirmed} />
                  <Stack.Screen
                    name="DietitianAddonOrderConfirmed"
                    component={DietitianAddonOrderConfirmedScreen}
                  />
                  <Stack.Screen
                    name="DiscountCode"
                    component={DiscountCodeModal}
                    options={fadeFromBottomModalOptions}
                  />
                  <Stack.Screen name="SupportAnswer" component={SupportAnswer} />
                  <Stack.Screen name="SupportArchive" component={SupportArchive} />
                  <Stack.Screen name="SupportHub" component={SupportHub} />
                  <Stack.Screen
                    name="Questionnaire"
                    component={QuestionnaireNavigator}
                    getId={({ params }) =>
                      (params as AppStackParamList['Questionnaire'] | undefined)?.questionnaire
                    }
                  />
                  <Stack.Screen
                    name="InsuranceQuestionnaire"
                    component={InsuranceQuestionnaireNavigator}
                  />
                  <Stack.Screen name="UpsellOffer" component={UpsellOfferScreen} />
                  <Stack.Screen
                    name="UnifiedOnboardingAppointmentOfferConfirmation"
                    component={UnifiedOnboardingAppointmentOfferConfirmation}
                  />
                  <Stack.Screen
                    name="UpsellOfferConfirmation"
                    component={UpsellOfferConfirmationScreen}
                  />
                  <Stack.Screen name="AppExplorationPrompt" component={AppExplorationPrompt} />
                  <Stack.Screen name="AppExplorationCarousel" component={AppExplorationCarousel} />
                  <Stack.Screen name="PermissionsCarousel" component={PermissionsCarousel} />
                  <Stack.Screen name="FreeVideoCall" component={FreeVideoCallScreen} />
                </>
              )}
            </Stack.Navigator>
          </NoraStreamChatProvider>
        </ActionBottomSheetProvider>
      </SceneContext.Provider>
    </BugsnagNavigationContainer>
  )
}

export const Navigation = createNavigator(MainApp)
